import { Plus, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  availableDocumentFieldTypes,
  canAddDocumentField,
  defaultDocumentField,
  documentFieldLabel,
  type ApplicationDocumentField,
  type ApplicationDocumentFieldType,
} from '@/lib/documentFields';

interface DocumentFieldsEditorProps {
  value: ApplicationDocumentField[];
  onChange: (next: ApplicationDocumentField[]) => void;
}

export function DocumentFieldsEditor({ value, onChange }: DocumentFieldsEditorProps): JSX.Element {
  const { t } = useLanguage();
  const addableTypes = availableDocumentFieldTypes(value);

  const handleAdd = () => {
    if (!canAddDocumentField(value)) return;
    const nextType = addableTypes[0] ?? 'resume';
    onChange([...value, defaultDocumentField(nextType)]);
  };

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleTypeChange = (index: number, type: ApplicationDocumentFieldType) => {
    onChange(
      value.map((row, i) =>
        i === index ? { type, required: row.required } : row,
      ),
    );
  };

  const handleRequiredChange = (index: number, required: boolean) => {
    onChange(value.map((row, i) => (i === index ? { ...row, required } : row)));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs font-medium">{t('positions.applicationDocuments')}</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 gap-1 text-xs"
          onClick={handleAdd}
          disabled={!canAddDocumentField(value) || addableTypes.length === 0}
        >
          <Plus className="h-3 w-3" />
          {t('positions.addDocumentField')}
        </Button>
      </div>

      {value.length === 0 ? (
        <p className="text-[11px] text-muted-foreground">{t('positions.applicationDocumentsEmpty')}</p>
      ) : (
        <div className="space-y-2">
          {value.map((row, index) => {
            const typesForRow = availableDocumentFieldTypes(
              value.filter((_, i) => i !== index),
            );
            const selectableTypes = typesForRow.includes(row.type)
              ? typesForRow
              : [row.type, ...typesForRow];

            return (
              <div key={`${row.type}-${index}`} className="flex items-center gap-2">
                <Select
                  value={row.type}
                  onValueChange={(v) => handleTypeChange(index, v as ApplicationDocumentFieldType)}
                >
                  <SelectTrigger className="h-8 w-[10rem] shrink-0 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {selectableTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {t(`positions.documentType.${type}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <span className="flex-1 min-w-0 text-[11px] text-muted-foreground truncate">
                  {t(`positions.documentTypeHint.${row.type}`)}
                </span>

                <div className="flex items-center gap-1.5 shrink-0">
                  <Label htmlFor={`document-required-${row.type}-${index}`} className="text-[10px] text-muted-foreground">
                    {t('positions.documentRequired')}
                  </Label>
                  <Switch
                    id={`document-required-${row.type}-${index}`}
                    checked={row.required}
                    onCheckedChange={(v) => handleRequiredChange(index, v)}
                  />
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  onClick={() => handleRemove(index)}
                  aria-label={t('positions.removeDocumentField')}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
